import { useState, useEffect } from "react";

import { producaov2 } from "../../../../../services/apis";
import { defaultFormValues } from "./constants";
import { GENERAL_SECTION_INPUTS } from "./GeneralDataSection";
import { INITIAL_TRAIL_SECTION_INPUTS } from "./InitialTrailSection";
import { MANAGER_SECTION_INPUTS } from "./ManagerSection";
import swal from "sweetalert";
import { THEME_SECTION_INPUTS } from "./ThemeSection";
import { createFormData, validateForm } from "./utils";
import { COURSES_SECTION_INPUTS } from "./CoursesSection";
import { FEATURES_SECTION_INPUTS } from "./FeaturesSection";

export default function useLmsForms({ lmsID, onSubmit, onClose }) {
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(!!lmsID);

  const isEditing = !!lmsID;

  const onChangeFormValue = (key, value) => {
    if (formErrors[key]) {
      setFormErrors((prev) => ({ ...prev, [key]: "" }));
    }

    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.persist();

    e.target?.scrollTo(0, e.target?.scrollHeight);

    setFormErrors({});
    setGeneralError("");

    let optionalKeys = [
      ...Object.values(INITIAL_TRAIL_SECTION_INPUTS),
      GENERAL_SECTION_INPUTS.TOTAL_COLLABORATORS,
      GENERAL_SECTION_INPUTS.DESCRIPTION,
      FEATURES_SECTION_INPUTS.BLOCKED_FEATURES
    ];

    if (isEditing) {
      optionalKeys = [
        ...optionalKeys,
        GENERAL_SECTION_INPUTS.IS_ACTIVE,
        MANAGER_SECTION_INPUTS.NEW_MANAGER_NAME,
        MANAGER_SECTION_INPUTS.NEW_MANAGER_EMAIL,
        MANAGER_SECTION_INPUTS.MANAGERS,
        MANAGER_SECTION_INPUTS.MANAGERS_EMAILS_TO_REMOVE,
      ];
    }

    const formValidation = validateForm({
      formValues,
      optionalKeys,
      isEditing,
    });

    if (formValidation.hasError) {
      const firstInputError = Object.keys(formValidation.errors)[0];

      e.target[firstInputError]?.focus();

      return setFormErrors(formValidation.errors);
    }

    const formData = createFormData({ formValues, isEditing });

    setIsLoadingSubmit(true);

    try {
      await producaov2({
        method: isEditing ? 'PUT' : 'POST',
        url: isEditing ? `/admin/incompany/update-lmsV2/${lmsID}` : '/admin/incompany/create-lmsV2',
        data: formData,
        headers: {
          access_token: `voitto-token ${localStorage.getItem("oauth_token")}`,
        },
      });

      swal({
        title: lmsID ? "Empresa atualizada com sucesso" : "Empresa criada com sucesso",
        icon: "success",
      });

      onSubmit();
    } catch (error) {
      setIsLoadingSubmit(false);

      if (error?.response) {
        if (error.response?.data?.message?.includes("url")) {
          setFormErrors((prev) => ({
            ...prev,
            [GENERAL_SECTION_INPUTS.URL]: error.response.data.message,
          }));

          e.target[GENERAL_SECTION_INPUTS.URL]?.focus();
        }

        if (error.response?.data?.message?.includes("gestor")) {
          setFormErrors((prev) => ({
            ...prev,
            [MANAGER_SECTION_INPUTS.NEW_MANAGER_EMAIL]:
              error.response.data.message,
          }));

          e.target[MANAGER_SECTION_INPUTS.NEW_MANAGER_EMAIL]?.focus();
        }

        if (error.response?.data?.message?.includes("logo")) {
          setFormErrors((prev) => ({
            ...prev,
            [THEME_SECTION_INPUTS.COMPANY_LOGO]: error.response.data.message,
          }));

          e.target[THEME_SECTION_INPUTS.COMPANY_LOGO]?.focus();
        }

        if (error.response?.data?.message?.includes("login")) {
          setFormErrors((prev) => ({
            ...prev,
            [THEME_SECTION_INPUTS.COMPANY_LOGIN_IMAGE]:
              error.response.data.message,
          }));

          e.target[THEME_SECTION_INPUTS.COMPANY_LOGIN_IMAGE]?.focus();
        }

        if (error.response?.data?.message?.includes("certificado")) {
          setFormErrors((prev) => ({
            ...prev,
            [THEME_SECTION_INPUTS.COMPANY_CERTIFICATE_LOGO]:
              error.response.data.message,
          }));

          e.target[THEME_SECTION_INPUTS.COMPANY_CERTIFICATE_LOGO]?.focus();
        }

        setGeneralError(error.response.data?.message);
        return;
      }

      setGeneralError("Houve um erro ao criar a empresa");
      console.log(error);
    }
  };

  useEffect(() => {
    const callback = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", callback);

    return () => {
      window.removeEventListener("keydown", callback);
    };
  }, []);

  useEffect(() => {
    if (!lmsID) {
      return;
    }

    const getLmsData = async () => {
      const response = await producaov2.get(`/admin/incompany-read/${lmsID}`, {
        headers: {
          access_token: `voitto-token ${localStorage.getItem("oauth_token")}`,
        },
      });

      setFormValues((prev) => ({
        ...prev,
        [GENERAL_SECTION_INPUTS.COMPANY_NAME]: response.data.name,
        [GENERAL_SECTION_INPUTS.DESCRIPTION]: response.data.description || '',
        [GENERAL_SECTION_INPUTS.TOTAL_LICENSES]: response.data.valid_employees,
        [GENERAL_SECTION_INPUTS.TOTAL_COLLABORATORS]: response.data.licenseUse,
        [GENERAL_SECTION_INPUTS.URL]: response.data.url,
        [GENERAL_SECTION_INPUTS.ACCESS_PERIOD]: response.data.expires_in,
        [GENERAL_SECTION_INPUTS.IS_ACTIVE]: +response.data.status === 2,
        [GENERAL_SECTION_INPUTS.STATUS]: +response.data.status === 2 ? 'active' : 'inactive',
        [GENERAL_SECTION_INPUTS.RESTRICTED_COURSES]: response.data.courses?.length > 0 ? 'yes' : 'no',
        [COURSES_SECTION_INPUTS.COURSES]: response.data.courses,
        [FEATURES_SECTION_INPUTS.BLOCKED_FEATURES]: response.data.blocked_features || [],
        [MANAGER_SECTION_INPUTS.MANAGERS]: response.data?.menagers?.map(
          (manager) => ({
            id: manager?.id_pessoa,
            name: manager?.pessoa?.nome || '(SEM NOME)',
            email: manager.email,
          })
        ),
        [THEME_SECTION_INPUTS.COMPANY_LOGO]: {
          url: response.data.logo,
        },
        [THEME_SECTION_INPUTS.COMPANY_LOGIN_IMAGE]: {
          url: response.data.url_image_login,
        },
        [THEME_SECTION_INPUTS.COMPANY_CERTIFICATE_LOGO]: {
          url: response.data.url_certificate_logo,
        },
        [THEME_SECTION_INPUTS.COMPANY_COLORS]: [
          {
            name: "(primária)",
            value: response.data.color_primary,
          },
          {
            name: "(secundária)",
            value: response.data.color_secondary,
          },
        ],
      }));

      setIsLoadingData(false);
    };

    getLmsData();
  }, [lmsID]);

  return {
    formValues,
    formErrors,
    generalError,
    isLoadingSubmit,
    isEditing,
    isLoadingData,
    onChangeFormValue,
    handleSubmit,
  };
}